import React, { useEffect, useRef, useState } from "react";
import { PageRenderer, type All8PageContent } from "@bcc/core-renderers";
import PreviewErrorBoundary from "./PreviewErrorBoundary";

/**
 * Le contenu de l'aperçu, rendu à l'identique du site tant que tout va bien, et bloc par bloc
 * dès qu'un bloc lève.
 *
 * Le rendu NORMAL passe par un seul <PageRenderer> : c'est lui qui connaît les règles
 * d'espacement entre blocs voisins, donc l'aperçu reste fidèle. Si un bloc lève, React démonte
 * l'arbre entier : la barrière rattrape, et on repasse alors sur un rendu bloc par bloc, où
 * seul le bloc fautif est remplacé par son encart. Les espacements y sont approximatifs (chaque
 * bloc est rendu comme s'il était seul), mais l'éditeur voit sa page au lieu d'une page blanche.
 *
 * La structure du DOM est la MÊME dans les deux modes : un div racine, un enfant par bloc. Le
 * marquage `data-editor-index` de l'aperçu s'appuie sur cette correspondance 1:1 pour relier un
 * bloc cliqué à sa carte.
 */
export type PagePreviewContentProps = { components: All8PageContent };

const PagePreviewContent: React.FC<PagePreviewContentProps> = ({ components }) => {
	const [blocParBloc, setBlocParBloc] = useState(false);
	const dernierContenu = useRef(components);

	// Toute EDITION redonne sa chance au rendu complet : un contenu corrigé retrouve aussitôt
	// l'aperçu fidèle, sans recharger l'éditeur. Au premier rendu, en revanche, on ne touche à
	// rien : l'effet de montage arrive APRES la capture d'erreur et annulerait la bascule.
	useEffect(() => {
		if (dernierContenu.current === components) return;
		dernierContenu.current = components;
		setBlocParBloc(false);
	}, [components]);

	if (!blocParBloc) {
		return (
			<PreviewErrorBoundary onError={() => setBlocParBloc(true)}>
				<PageRenderer components={components} />
			</PreviewErrorBoundary>
		);
	}

	return (
		<div>
			{components.map((component, index) => (
				<PreviewErrorBoundary
					key={component.key ?? `cmp-${index}`}
					label={component.componentType}
				>
					<PageRenderer components={[component]} />
				</PreviewErrorBoundary>
			))}
		</div>
	);
};

export default PagePreviewContent;
